/**
 * Embedded Stripe Elements checkout for subscription plans.
 * URL: /subscribe?plan=monthly|yearly
 * Flow:
 *   1. On mount → POST /subscription/intent → get client_secret + publishable_key
 *   2. Render <Elements> provider + <PaymentElement>
 *   3. On submit → stripe.confirmPayment({ return_url })
 *   4. Return URL hits /subscribe/success?sub={subscription_id}&payment_intent=... → verify-by-sub
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import {
  Lock,
  ArrowLeft,
  CheckCircle,
  ShieldCheck,
  CurrencyEur,
} from '@phosphor-icons/react';
import Logo from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------- Plan definitions (client-side mirror) ----------------
const PLANS = {
  monthly: { label: 'Mensile', amount: 6.99, interval: 'mese', sub: 'Fatturato ogni mese, cancelli quando vuoi' },
  yearly: { label: 'Annuale', amount: 49.99, interval: 'anno', sub: 'Risparmi 40% · cancelli quando vuoi', badge: 'Consigliato' },
};

// ---------------- Stripe Elements appearance (dark minimal) ----------------
const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#18181b',
    colorBackground: '#ffffff',
    colorText: '#18181b',
    colorDanger: '#dc2626',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    borderRadius: '0px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '1px solid #d4d4d8',
      boxShadow: 'none',
      padding: '12px',
    },
    '.Input:focus': {
      border: '1px solid #18181b',
      boxShadow: 'none',
    },
    '.Label': {
      fontWeight: '700',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: '#71717a',
      marginBottom: '6px',
    },
  },
};

// ---------------- Payment form (inside <Elements>) ----------------
function PaymentForm({ plan, subscriptionId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const returnUrl = `${window.location.origin}/subscribe/success?sub=${subscriptionId}`;
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    // We only reach this point if there's an immediate error.
    // On success, Stripe redirects to return_url.
    if (stripeError) {
      setError(stripeError.message || 'Errore durante il pagamento');
      toast.error(stripeError.message || 'Errore durante il pagamento');
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="stripe-form">
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-800">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-black uppercase tracking-wider text-sm py-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="submit-payment-btn"
      >
        {submitting ? (
          'Elaborazione...'
        ) : (
          <>
            <Lock size={16} weight="bold" />
            Paga € {plan.amount.toFixed(2)} · {plan.interval === 'mese' ? 'mensile' : 'annuale'}
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-4 pt-2 text-[10px] text-zinc-400 uppercase tracking-wider">
        <span className="inline-flex items-center gap-1">
          <ShieldCheck size={12} weight="bold" /> Protetto da Stripe
        </span>
        <span>·</span>
        <span>3D Secure</span>
        <span>·</span>
        <span>PCI compliant</span>
      </div>
    </form>
  );
}

// ---------------- Main page ----------------
export default function SubscribePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const planId = params.get('plan') === 'yearly' ? 'yearly' : 'monthly';
  const plan = PLANS[planId];

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);

  useEffect(() => {
    document.title = `Abbonati · ${plan.label} · Locazione Turistica`;
    if (!user) {
      navigate('/login?next=' + encodeURIComponent('/subscribe?plan=' + planId));
      return;
    }
    (async () => {
      try {
        const [pubRes, intentRes] = await Promise.all([
          axios.get(`${API}/subscription/public-key`),
          axios.post(
            `${API}/subscription/intent`,
            { package_id: planId, origin_url: window.location.origin },
            { withCredentials: true }
          ),
        ]);
        const pk = pubRes.data?.publishable_key;
        if (!pk) throw new Error('Stripe non configurato');
        setStripePromise(loadStripe(pk));
        setClientSecret(intentRes.data.client_secret);
        setSubscriptionId(intentRes.data.subscription_id);
      } catch (e) {
        console.error('Intent error:', e);
        setFatal(e?.response?.data?.detail || e?.message || 'Errore inizializzazione pagamento');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900" data-testid="subscribe-page">
      {/* Nav */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="subscribe-back">
            <Logo className="h-6 w-auto" />
          </Link>
          <Link
            to="/dashboard"
            className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} weight="bold" /> Torna indietro
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 lg:gap-12">
          {/* Left: plan summary & trust signals */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-600 mb-3">
              Completa l'abbonamento
            </p>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-4">
              Sblocca tutto<br />in 30 secondi.
            </h1>
            <p className="text-sm text-zinc-500 leading-relaxed max-w-md mb-8">
              Dopo il pagamento torni subito alla tua area personale. Cancelli quando vuoi dalla tua pagina abbonamento — niente vincoli nascosti.
            </p>

            {/* Plan card */}
            <div className="bg-white border border-zinc-200 p-5 sm:p-6 mb-6" data-testid="plan-summary">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400 mb-1">Piano selezionato</p>
                  <p className="text-xl font-black text-zinc-900">{plan.label}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{plan.sub}</p>
                </div>
                {plan.badge && (
                  <span className="bg-emerald-500 text-white text-[10px] font-black uppercase tracking-wider px-2 py-1">
                    {plan.badge}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2 pt-4 border-t border-zinc-100">
                <CurrencyEur size={22} weight="bold" className="text-zinc-900 mb-1" />
                <p className="text-4xl sm:text-5xl font-black text-zinc-900 tracking-tight leading-none tabular-nums">
                  {plan.amount.toFixed(2).replace('.', ',')}
                </p>
                <p className="text-sm text-zinc-500 ml-1">/ {plan.interval}</p>
              </div>

              {/* Plan switcher */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-100 text-xs">
                <span className="text-zinc-400">Cambia piano:</span>
                {planId === 'monthly' ? (
                  <Link to="/subscribe?plan=yearly" className="font-bold text-zinc-900 underline hover:text-emerald-600" data-testid="switch-to-yearly">
                    Passa ad annuale → risparmia 40%
                  </Link>
                ) : (
                  <Link to="/subscribe?plan=monthly" className="font-bold text-zinc-500 underline hover:text-zinc-900" data-testid="switch-to-monthly">
                    Passa al mensile
                  </Link>
                )}
              </div>
            </div>

            {/* Trust bullets */}
            <ul className="space-y-2.5 text-xs text-zinc-600">
              {[
                'Pagamento sicuro — gestito interamente da Stripe',
                'Fatturazione automatica con ricevuta via email',
                'Cancelli in 2 click dalla pagina abbonamento',
                'Rimborso entro 7 giorni se non ti piace',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Stripe Elements form */}
          <div>
            <div className="bg-white border border-zinc-200 p-5 sm:p-7 lg:sticky lg:top-8">
              <div className="flex items-center gap-2 mb-5 pb-5 border-b border-zinc-100">
                <Lock size={16} weight="bold" className="text-zinc-700" />
                <h2 className="text-sm font-black uppercase tracking-wider text-zinc-900">
                  Dati di pagamento
                </h2>
              </div>

              {loading && (
                <div className="py-12 text-center text-sm text-zinc-400" data-testid="stripe-loading">
                  <div className="inline-block w-6 h-6 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mb-3" />
                  <p>Inizializzazione pagamento sicuro...</p>
                </div>
              )}

              {!loading && fatal && (
                <div className="py-8 text-center" data-testid="stripe-error">
                  <p className="text-sm font-bold text-red-700 mb-2">Impossibile inizializzare il pagamento</p>
                  <p className="text-xs text-zinc-500 mb-4">{fatal}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-xs font-bold uppercase tracking-wider text-zinc-900 underline"
                  >
                    Riprova
                  </button>
                </div>
              )}

              {!loading && !fatal && clientSecret && stripePromise && (
                <Elements key={planId} stripe={stripePromise} options={{ clientSecret, appearance, locale: 'it' }}>
                  <PaymentForm plan={plan} subscriptionId={subscriptionId} />
                </Elements>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
